import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Car as CarIcon, Plane, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

export function AirportHosts({ compact = false }) {
  const [hosts, setHosts] = useState(null);

  useEffect(() => {
    api.get("/airport-hosts").then((r) => setHosts(r.data)).catch(() => setHosts([]));
  }, []);

  if (hosts === null)
    return <p className="text-slate-400 flex items-center gap-2 py-8 justify-center" data-testid="airport-hosts-loading"><Loader2 size={16} className="animate-spin" /> Loading hosts…</p>;

  if (hosts.length === 0)
    return (
      <div data-testid="airport-hosts-empty" className="text-center bg-slate-50 border border-slate-200 rounded-2xl p-10">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-blue-50 text-blue-600 mx-auto mb-4"><Plane size={22} /></span>
        <p className="font-heading text-lg font-bold text-slate-900">Airport hosts coming soon</p>
        <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">We're onboarding hosts who offer Logan Airport pickup &amp; return. In the meantime, browse the full fleet and book online.</p>
        <Link to="/fleet" className="inline-flex items-center gap-2 mt-5 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors">Browse the fleet <ArrowRight size={16} /></Link>
      </div>
    );

  return (
    <div className={`grid gap-6 ${compact ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="airport-hosts-grid">
      {hosts.map((h) => (
        <Link
          key={h.id}
          to={`${h.profile_url}?airport=1`}
          data-testid={`airport-host-${h.id}`}
          className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-blue-300 hover:shadow-card transition-all"
        >
          <div className="flex items-center gap-4">
            {h.profile_photo ? (
              <img src={h.profile_photo} alt={h.name} className="w-16 h-16 rounded-full object-cover border border-slate-200" data-testid={`airport-host-photo-${h.id}`} />
            ) : (
              <span className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 grid place-items-center font-heading text-2xl font-bold text-slate-400">{(h.name || "?").charAt(0).toUpperCase()}</span>
            )}
            <div className="min-w-0">
              <p className="font-heading text-lg font-bold text-slate-900 truncate">{h.name}</p>
              {h.city && <p className="text-slate-500 text-sm truncate">{h.city}</p>}
              <div className="flex items-center gap-3 mt-1 text-sm">
                {h.rating?.count > 0
                  ? <span className="inline-flex items-center gap-1 text-slate-700"><Star size={13} className="text-amber-400 fill-amber-400" /> {h.rating.avg.toFixed(1)} <span className="text-slate-400">({h.rating.count})</span></span>
                  : <span className="text-slate-400 text-xs">New host</span>}
                <span className="inline-flex items-center gap-1 text-slate-600 text-xs"><CarIcon size={13} className="text-slate-400" /> {h.vehicles_count} {h.vehicles_count === 1 ? "vehicle" : "vehicles"}</span>
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-3 py-1"><Plane size={12} /> Airport {h.airport_fee > 0 ? money(h.airport_fee) : "included"}</span>
            <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">View &amp; book <ArrowRight size={15} /></span>
          </div>
        </Link>
      ))}
    </div>
  );
}
